import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useFetch from "../../../hooks/useFetch";
import usePost from "../../../hooks/usePost";
import usePut from "../../../hooks/usePut";
import { useCurrent } from "../../../hooks";
import { MwButton, MwToast } from "../../ui";
import MwSelectorAccount from "../../ui/MwSelectorAccount";
import { useAccountSettings } from "../../../contexts/AccountSettingsContext";

const AddEditTaxModal = ({
  isOpen,
  onClose,
  mode,
  itemId,
  onSuccess,
}) => {
  const { t } = useTranslation("global");
  const { currentTenantId, currentCompanyId, currentBranchId } = useCurrent();
  const { getChartAccountLevelLimit } = useAccountSettings();
  const chartLevelLimit = getChartAccountLevelLimit();

  const [formData, setFormData] = useState({
    taxNameAr: "",
    taxNameEn: "",
    taxValue: "",
    isActive: true,
    tenantId: currentTenantId,
    companyId: currentCompanyId,
    branchId: currentBranchId,
    tax_type: "",
    account_no: "",
  });

  const [isLoading, setIsLoading] = useState(mode === "edit");
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const { postData } = usePost();
  const { putData } = usePut();

  const shouldFetchDetails = mode === "edit" && itemId;
  const detailUrl = shouldFetchDetails
    ? `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/taxes/${itemId}`
    : null;
  const { data: taxDetails, loading: fetchLoading } = useFetch(
    detailUrl,
    shouldFetchDetails
  );

  useEffect(() => {
    setIsLoading(fetchLoading);
  }, [fetchLoading]);

  useEffect(() => {
    if (mode === "edit" && taxDetails?.data) {
      const taxData = taxDetails.data;
      setFormData({
        taxNameAr: taxData.tax_name_ar?.trim() || "",
        taxNameEn: taxData.tax_name_en?.trim() || "",
        taxValue: taxData.tax_value || "",
        isActive: taxData.is_active,
        tenantId: taxData.tenant_id || currentTenantId,
        companyId: taxData.company_id || currentCompanyId,
        branchId: taxData.branch_id || currentBranchId,
        tax_type: taxData.tax_type || "",
        account_no: taxData.account_no || "",
      });
      setIsLoading(false);
    } else if (mode === "add") {
      setFormData({
        taxNameAr: "",
        taxNameEn: "",
        taxValue: "",
        isActive: true,
        tenantId: currentTenantId,
        companyId: currentCompanyId,
        branchId: currentBranchId,
        tax_type: "",
        account_no: "",
      });
      setIsLoading(false);
    }
  }, [taxDetails, mode, currentTenantId, currentCompanyId, currentBranchId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await handleConfirm();
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const dataToSubmit = {
        ...formData,
        taxValue: parseFloat(formData.taxValue),
        tax_type: formData.tax_type || "ALL",
        account_no: formData.account_no || "",
      };

      if (mode === "add") {
        await postData(
          `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/taxes`,
          dataToSubmit
        );
        setToastMessage(t("vat.addSuccess", "تم إضافة الضريبة بنجاح"));
        setShowToast(true);
      } else {
        await putData(
          `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/taxes/update/${itemId}`,
          dataToSubmit
        );
        setToastMessage(t("vat.updateSuccess", "تم تحديث الضريبة بنجاح"));
        setShowToast(true);
      }

      setIsLoading(false);
      onSuccess();
      onClose();
    } catch (error) {
      setToastMessage(t("common.errorSaving", "حدث خطأ أثناء الحفظ. يرجى المحاولة مرة أخرى."));
      setShowToast(true);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-gray-800 bg-opacity-50 flex justify-center items-center text-xs">
      <MwToast msg={toastMessage} isShow={showToast} setIsShow={setShowToast} />
      <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6 relative text-slate-700">
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col justify-center items-center rounded-lg z-10">
            <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin"></div>
            <p className="mt-4 text-blue-600 font-medium">
              {mode === "edit"
                ? t("common.loading")
                : t("common.saving")}
            </p>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-slate-600 text-base">
            {mode === "add" ? t("vat.addNew") : t("vat.edit")}
          </h2>
          <button
            onClick={onClose}
            type="button"
            className="text-slate-600 hover:text-slate-800"
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleFormSubmit}>
          <div className="mb-4">
            <label className="block text-slate-600 text-sm mb-2">
              {t("vat.nameAr")}
            </label>
            <input
              type="text"
              name="taxNameAr"
              value={formData.taxNameAr}
              onChange={handleChange}
              className="w-full p-2 border rounded text-slate-700"
              required
              disabled={isLoading}
            />
          </div>

          <div className="mb-4">
            <label className="block text-slate-600 text-sm mb-2">
              {t("vat.nameEn")}
            </label>
            <input
              type="text"
              name="taxNameEn"
              value={formData.taxNameEn}
              onChange={handleChange}
              className="w-full p-2 border rounded text-slate-700"
              required
              disabled={isLoading}
            />
          </div>

          <div className="mb-4">
            <label className="block text-slate-600 text-sm mb-2">
              {t("vat.value")}
            </label>
            <input
              type="number"
              name="taxValue"
              value={formData.taxValue}
              onChange={handleChange}
              className="w-full p-2 border rounded text-slate-700"
              min="0"
              step="0.01"
              required
              disabled={isLoading}
            />
          </div>

          <div className="mb-4">
            <label className="block text-slate-600 text-sm mb-2">
              {t("vat.taxType", "نوع الضريبة")}
            </label>
            <select
              name="tax_type"
              value={formData.tax_type}
              onChange={handleChange}
              className="w-full p-2 border rounded text-slate-700 bg-white"
              disabled={isLoading}
            >
              <option value="">{t("vat.taxTypeAll", "ALL (الكل)")}</option>
              <option value="SALES">{t("vat.taxTypeSales", "SALES (المبيعات)")}</option>
              <option value="PURCHASE">{t("vat.taxTypePurchase", "PURCHASE (المشتريات)")}</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-slate-600 text-sm mb-2">
              {t("vat.accountNumber", "رقم الحساب")}
            </label>
            <MwSelectorAccount
              key={`${mode}-${itemId || "new"}-${formData.account_no || "empty"}`}
              withLabel={false}
              placeholder={t("vat.selectSubAccount", "اختر الحساب الفرعي")}
              classNamePrivate="w-full"
              initialValue={formData.account_no || ""}
              onChangeCallback={(selectedAccount) => {
                setFormData((prev) => ({
                  ...prev,
                  account_no: selectedAccount?.account_no || "",
                }));
              }}
              levelLimit={chartLevelLimit}
            />
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="mr-2"
                disabled={isLoading}
              />
              <span className="text-slate-600 text-sm">
                {t("vat.active")}
              </span>
            </label>
          </div>

          <div className="flex justify-end gap-2">
            <MwButton
              type="cancelBtn"
              size="sm"
              onClick={onClose}
              className="mr-2 px-4 py-2 text-slate-600 hover:text-slate-800"
              disabled={isLoading}
            >
              {t("common.cancel")}
            </MwButton>
            <MwButton
              type="bottomBtn"
              size="sm"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              disabled={isLoading}
            >
              {mode === "add" ? t("common.add") : t("common.save")}
            </MwButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditTaxModal;
